import { useContext, useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { UserContext } from "../contexts/UserContext";
import { hasPrivilegedAccess } from "../utils/privilegedAccess";
import {
  formatNotificationCount,
  isAccountantApprover,
  isAdminStaffSubmission,
  isUnseenLeaveDecision,
} from "../utils/timesheetNotifications";
import greenspaceLogo from "../assets/GREENSPACE_INDUSTRIES_LOGO_ONLY_3.png";

const linkBaseCls =
  "relative inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 no-underline hover:text-black hover:bg-gray-100 transition";

const mobileLinkCls =
  "px-3 py-2 rounded-lg text-sm font-medium text-gray-700 no-underline hover:bg-gray-100 hover:text-black transition";

function NotificationBadge({ count, variant = "desktop" }) {
  const label = formatNotificationCount(count);
  if (!label) return null;

  const baseCls =
    "inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-bold leading-none text-white shadow-sm";
  const positionCls =
    variant === "desktop"
      ? "absolute -right-1.5 -top-2 h-5 ring-2 ring-white"
      : "ml-3 h-5";

  return (
    <span className={`${baseCls} ${positionCls}`} aria-label={`${label} notifications`}>
      {label}
    </span>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useContext(UserContext);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({
    adminTimesheets: 0,
    adminLeave: 0,
    driverLeaveDecisions: 0,
  });

  const handleLogout = async () => {
    await signOut(auth);
    setMobileOpen(false);
    navigate("/login", { replace: true });
  };

  const role = String(userInfo?.role || "").toLowerCase();
  const isDriver = role === "driver";
  const isAdmin = role === "admin";
  const isAccountant = isAccountantApprover(userInfo);
  const canManageUsers = hasPrivilegedAccess(userInfo);

  useEffect(() => {
    setNotificationCounts({
      adminTimesheets: 0,
      adminLeave: 0,
      driverLeaveDecisions: 0,
    });

    if (isAdmin) {
      let adminTimesheets = 0;
      let adminLeave = 0;
      const setAdminCounts = () => {
        setNotificationCounts({
          adminTimesheets,
          adminLeave,
          driverLeaveDecisions: 0,
        });
      };

      const unsubscribeTimesheets = onSnapshot(
        query(collection(db, "timesheets"), where("status", "==", "submitted")),
        (snap) => {
          adminTimesheets = snap.docs
            .map((item) => item.data())
            .filter((item) => isAccountant || !isAdminStaffSubmission(item)).length;
          setAdminCounts();
        },
        (error) => {
          console.warn("Failed to listen for timesheet notifications:", error);
        },
      );

      const unsubscribeLeave = onSnapshot(
        query(collection(db, "leaveRequests"), where("status", "in", ["pending", "submitted"])),
        (snap) => {
          adminLeave = snap.docs
            .map((item) => item.data())
            .filter((item) => isAccountant || !isAdminStaffSubmission(item)).length;
          setAdminCounts();
        },
        (error) => {
          console.warn("Failed to listen for leave request notifications:", error);
        },
      );

      return () => {
        unsubscribeTimesheets();
        unsubscribeLeave();
      };
    }

    if (isDriver && userInfo?.uid) {
      return onSnapshot(
        query(collection(db, "leaveRequests"), where("driverId", "==", userInfo.uid)),
        (snap) => {
          const driverLeaveDecisions = snap.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .filter(isUnseenLeaveDecision).length;

          setNotificationCounts({
            adminTimesheets: 0,
            adminLeave: 0,
            driverLeaveDecisions,
          });
        },
        (error) => {
          console.warn("Failed to listen for driver leave notifications:", error);
        },
      );
    }
  }, [isAccountant, isAdmin, isDriver, userInfo?.uid]);

  const isActive = (path) => location.pathname === path;

  const userAccessLinks = canManageUsers
    ? [
        { to: "/signup", label: "Sign up" },
        { to: "/admin/reset-password", label: "Reset password" },
      ]
    : [];

  const driverLinks = [
    { to: "/dashboard", label: "My jobs" },
    { to: "/driver/calendar", label: "Calendar" },
    { to: "/driver/incidents", label: "Incidents" },
    { to: "/documents", label: "Documents" },
    {
      to: "/driver/timesheet",
      label: "Timesheet",
      badge: notificationCounts.driverLeaveDecisions,
    },
    ...userAccessLinks,
  ];

  const adminTimesheetNotifications =
    notificationCounts.adminTimesheets + notificationCounts.adminLeave;

  const adminLinks = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/calendar", label: "Calendar" },
    { to: "/admin/live-map", label: "Live map" },
    { to: "/admin/incidents", label: "Incidents" },
    { to: "/documents", label: "Documents" },
    { to: "/driver/timesheet", label: "My timesheet" },
    ...(isAdmin ? [{ to: "/admin/driver-view", label: "Driver view" }] : []),
    ...(isAdmin
      ? [
          {
            to: "/admin/timesheets",
            label: "Timesheets",
            badge: adminTimesheetNotifications,
          },
        ]
      : []),
    ...(isAdmin ? [{ to: "/admin/data", label: "Data hub" }] : []),
    ...(isAdmin ? [{ to: "/admin/managers", label: "Managers" }] : []),
    ...userAccessLinks,
  ];

  const linksToRender = isDriver ? driverLinks : adminLinks;

  const handleNavClick = () => {
    setMobileOpen(false);
  };

  const homePath = isDriver ? "/dashboard" : "/dashboard";

  return (
    <nav className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur">
      <div className="w-full px-4 sm:px-6 xl:px-8">
        <div className="flex h-16 items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to={homePath}
              className="flex items-center gap-3 no-underline"
              onClick={handleNavClick}
            >
              <img
                src={greenspaceLogo}
                alt="Greenspace logo"
                className="h-11 w-11 object-contain"
              />
              <div className="hidden sm:block">
                <div className="text-sm font-semibold text-gray-900">
                  Greenspace Industries
                </div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-gray-500">
                  {isDriver ? "Driver app" : "Operations app"}
                </div>
              </div>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2 sm:gap-3">
            {linksToRender.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={handleNavClick}
                className={`${linkBaseCls} ${
                  isActive(link.to)
                    ? "bg-gray-900 text-white hover:bg-black"
                    : ""
                }`}
              >
                <span>{link.label}</span>
                <NotificationBadge count={link.badge} />
              </Link>
            ))}

            {userInfo?.email && (
              <span className="hidden lg:inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                {userInfo.email}
              </span>
            )}

            <button
              onClick={handleLogout}
              className="inline-flex items-center rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700"
            >
              Logout
            </button>
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 p-2 text-gray-700 transition hover:bg-gray-100 md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            <span className="sr-only">Toggle navigation</span>
            <div className="flex flex-col gap-1.5">
              <span className="block h-0.5 w-5 rounded bg-gray-800" />
              <span className="block h-0.5 w-5 rounded bg-gray-800" />
              <span className="block h-0.5 w-5 rounded bg-gray-800" />
            </div>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-gray-200 bg-white md:hidden">
          <div className="w-full space-y-2 px-4 py-3 sm:px-6 xl:px-8">
            {linksToRender.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={handleNavClick}
                className={`flex w-full items-center justify-between text-left ${mobileLinkCls} ${
                  isActive(link.to)
                    ? "bg-gray-900 text-white hover:bg-black"
                    : ""
                }`}
              >
                <span>{link.label}</span>
                <NotificationBadge count={link.badge} variant="mobile" />
              </Link>
            ))}

            {userInfo?.email && (
              <div className="pt-1">
                <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {userInfo.email}
                </div>
              </div>
            )}

            <button
              onClick={handleLogout}
              className="mt-1 inline-flex w-full items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
